import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Collapse,
  TextField,
  Button,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TimerIcon from "@mui/icons-material/Timer";
import RouteIcon from "@mui/icons-material/Route";
import SpeedIcon from "@mui/icons-material/Speed";
import DownloadIcon from "@mui/icons-material/Download";
import {
  GoogleMap,
  Polyline,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { format } from "date-fns";
import { mapStyles, mapOptions } from "./vehicleTrack/styles";
import {
  formatTime,
  getUserTimeFormat,
  getUserDateFormat,
  parseReportDate,
} from "../../utils/formatDate.js";

const MINI_MAP_HEIGHT = 400;

const miniMapContainerStyle = {
  width: "100%",
  height: `${MINI_MAP_HEIGHT}px`,
};

// ─── Shared date/time helpers ─────────────────────────────────────────────────

/**
 * Format a "YYYY/MM/DD" trip date string using the user's date format preference.
 * Mirrors the formatDateOnly pattern used in AlertItem.
 */
const formatTripDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const parsed = parseReportDate(dateStr);
    const dateObj = parsed?.date || new Date(dateStr);
    if (isNaN(dateObj.getTime())) return dateStr;

    const userDateFormat = getUserDateFormat();
    let compactFormat;
    switch (userDateFormat) {
      case "MM/DD/YYYY":
        compactFormat = "MMM dd, yyyy";
        break;
      case "YYYY/MM/DD":
        compactFormat = "yyyy MMM dd";
        break;
      case "DD/MM/YYYY":
      default:
        compactFormat = "dd MMM yyyy";
        break;
    }
    return format(dateObj, compactFormat);
  } catch (error) {
    console.error("Error formatting trip date:", error);
    return dateStr;
  }
};

/**
 * Format a "HH:MM:SS" time string using the user's time format preference.
 * Needed because trip times arrive as plain strings, not Date objects.
 */
const formatTripTime = (timeStr) => {
  if (!timeStr) return "";
  const timeFormat = getUserTimeFormat();
  if (timeFormat !== "12hr") return timeStr; // return as-is for 24hr
  const [hours, minutes, seconds] = timeStr.split(":");
  const h = parseInt(hours, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = ((h + 11) % 12) + 1;
  return seconds
    ? `${hour12}:${minutes}:${seconds} ${suffix}`
    : `${hour12}:${minutes} ${suffix}`;
};

/**
 * Format a timestamp (ms) into date string using user's date format preference.
 * Used for waypoint dots on the mini map.
 */
const formatWaypointDate = (timestamp) => {
  try {
    const dateObj = new Date(timestamp);
    const userDateFormat = getUserDateFormat();
    let compactFormat;
    switch (userDateFormat) {
      case "MM/DD/YYYY": compactFormat = "MMM dd, yyyy"; break;
      case "YYYY/MM/DD": compactFormat = "yyyy MMM dd";  break;
      default:           compactFormat = "dd MMM yyyy";  break;
    }
    return format(dateObj, compactFormat);
  } catch {
    return new Date(timestamp).toDateString();
  }
};

// ─── Haversine helper ─────────────────────────────────────────────────────────
const haversineMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// A missed or bad GPS fix is joined with a straight line, which cuts across
// the map. Drop fixes that would require an impossible speed, and start a new
// stroke when the tracker was silent long enough for the gap to be misleading.
const MAX_PATH_SPEED_KMH = 180;
const MAX_PATH_GAP_METERS = 1200;

const cleanTraveledPath = (path) => {
  const sorted = [...path]
    .filter((point) => Number.isFinite(point?.lat) && Number.isFinite(point?.lng))
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  const kept = [];
  sorted.forEach((point) => {
    const previous = kept[kept.length - 1];
    if (!previous) {
      kept.push(point);
      return;
    }
    const distance = haversineMeters(previous.lat, previous.lng, point.lat, point.lng);
    const seconds = ((point.timestamp || 0) - (previous.timestamp || 0)) / 1000;
    if (seconds > 0) {
      const speedKmh = (distance / seconds) * 3.6;
      if (speedKmh > MAX_PATH_SPEED_KMH) return;
    } else if (distance > 30) {
      return;
    }
    kept.push({ ...point, breakBefore: distance > MAX_PATH_GAP_METERS });
  });
  return kept;
};

const splitTraveledPath = (path) => {
  const segments = [];
  let current = [];
  cleanTraveledPath(path).forEach((point) => {
    if (point.breakBefore && current.length > 1) {
      segments.push(current);
      current = [];
    }
    current.push(point);
  });
  if (current.length > 1) segments.push(current);
  return segments;
};

const getTripSummary = (path) => {
  let distanceMeters = 0;

  for (let index = 1; index < path.length; index += 1) {
    const previous = path[index - 1];
    const current = path[index];
    distanceMeters += haversineMeters(
      previous.lat,
      previous.lng,
      current.lat,
      current.lng,
    );
  }

  return { distanceKm: distanceMeters / 1000 };
};

const drawRoundedRect = (context, x, y, width, height, radius) => {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
  context.fill();
};

const TILE_SIZE = 256;

const latLngToWorld = (lat, lng) => {
  const siny = Math.sin((lat * Math.PI) / 180);
  const clamped = Math.min(Math.max(siny, -0.9999), 0.9999);
  return {
    x: TILE_SIZE * (0.5 + lng / 360),
    y: TILE_SIZE * (0.5 - Math.log((1 + clamped) / (1 - clamped)) / (4 * Math.PI)),
  };
};

const worldToZoomPixel = (lat, lng, zoom) => {
  const world = latLngToWorld(lat, lng);
  const scale = 2 ** zoom;
  return { x: world.x * scale, y: world.y * scale };
};

const zoomToFit = (minLat, maxLat, minLng, maxLng, width, height, padding) => {
  for (let zoom = 18; zoom >= 3; zoom -= 1) {
    const northWest = worldToZoomPixel(maxLat, minLng, zoom);
    const southEast = worldToZoomPixel(minLat, maxLng, zoom);
    if (
      southEast.x - northWest.x <= width - padding * 2 &&
      southEast.y - northWest.y <= height - padding * 2
    ) {
      return zoom;
    }
  }
  return 3;
};

const loadMapTile = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Map tile failed"));
    image.src = url;
  });

const drawRouteBaseMap = async (context, bounds, path) => {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  path.forEach((point) => {
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLng = Math.min(minLng, point.lng);
    maxLng = Math.max(maxLng, point.lng);
  });

  const padding = 40;
  const zoom = zoomToFit(minLat, maxLat, minLng, maxLng, bounds.width, bounds.height, padding);
  const northWest = worldToZoomPixel(maxLat, minLng, zoom);
  const southEast = worldToZoomPixel(minLat, maxLng, zoom);
  const offsetX = bounds.x + (bounds.width - (southEast.x - northWest.x)) / 2 - northWest.x;
  const offsetY = bounds.y + (bounds.height - (southEast.y - northWest.y)) / 2 - northWest.y;
  const worldLeft = bounds.x - offsetX;
  const worldTop = bounds.y - offsetY;
  const worldRight = bounds.x + bounds.width - offsetX;
  const worldBottom = bounds.y + bounds.height - offsetY;
  const maxTile = 2 ** zoom;
  const tileLeft = Math.floor(worldLeft / TILE_SIZE);
  const tileTop = Math.max(0, Math.floor(worldTop / TILE_SIZE));
  const tileRight = Math.floor((worldRight - 1) / TILE_SIZE);
  const tileBottom = Math.min(maxTile - 1, Math.floor((worldBottom - 1) / TILE_SIZE));

  context.save();
  context.beginPath();
  context.roundRect(bounds.x, bounds.y, bounds.width, bounds.height, 18);
  context.clip();
  context.fillStyle = "#d5dde6";
  context.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);

  const tiles = [];
  for (let tileX = tileLeft; tileX <= tileRight; tileX += 1) {
    for (let tileY = tileTop; tileY <= tileBottom; tileY += 1) {
      const wrappedX = ((tileX % maxTile) + maxTile) % maxTile;
      const url = `https://tile.openstreetmap.org/${zoom}/${wrappedX}/${tileY}.png`;
      tiles.push(
        loadMapTile(url)
          .then((image) => ({
            image,
            x: tileX * TILE_SIZE + offsetX,
            y: tileY * TILE_SIZE + offsetY,
          }))
          .catch(() => null),
      );
    }
  }

  const loadedTiles = (await Promise.all(tiles)).filter(Boolean);
  context.filter = "grayscale(0.72) saturate(0.18) brightness(1.06) contrast(0.96)";
  loadedTiles.forEach(({ image, x, y }) => {
    context.drawImage(image, x, y, TILE_SIZE, TILE_SIZE);
  });
  context.filter = "none";

  if (loadedTiles.length === 0) {
    context.fillStyle = "rgba(7,20,38,0.92)";
    context.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
    context.fillStyle = "rgba(255,255,255,0.08)";
    for (let x = bounds.x; x < bounds.x + bounds.width; x += 80) {
      context.fillRect(x, bounds.y, 1, bounds.height);
    }
    for (let y = bounds.y; y < bounds.y + bounds.height; y += 80) {
      context.fillRect(bounds.x, y, bounds.width, 1);
    }
  }

  context.restore();

  return (point) => {
    const pixel = worldToZoomPixel(point.lat, point.lng, zoom);
    return { x: pixel.x + offsetX, y: pixel.y + offsetY };
  };
};

const createTripSummaryImage = async ({ trip, historicalPath, vehicle, route }) => {
  const validPath = cleanTraveledPath(historicalPath);

  if (validPath.length < 2) {
    throw new Error("At least two route points are required to export a trip image.");
  }

  const canvas = document.createElement("canvas");
  canvas.width = 1600;
  canvas.height = 1000;
  const context = canvas.getContext("2d");
  const summary = getTripSummary(validPath);
  const vehicleName = vehicle?.name || vehicle?.vehicleName || "Vehicle";
  const vehicleNumber = vehicle?.VehicleNumber || vehicle?.vehicleNumber || "";

  const background = context.createLinearGradient(0, 0, canvas.width, canvas.height);
  background.addColorStop(0, "#071426");
  background.addColorStop(1, "#183059");
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.fillStyle = "#ff6b00";
  context.font = "700 28px Arial";
  context.fillText("ROUTEEYE · TRIP SUMMARY", 72, 72);
  context.fillStyle = "#ffffff";
  context.font = "700 60px Arial";
  context.fillText(vehicleName, 72, 145);
  context.fillStyle = "#a9bdd5";
  context.font = "400 25px Arial";
  context.fillText(
    [vehicleNumber, route?.name, formatTripDate(trip.date)].filter(Boolean).join("  •  "),
    75,
    190,
  );

  const mapBounds = { x: 90, y: 250, width: 920, height: 500 };
  const project = await drawRouteBaseMap(context, mapBounds, validPath);
  context.save();
  context.beginPath();
  context.roundRect(mapBounds.x, mapBounds.y, mapBounds.width, mapBounds.height, 18);
  context.clip();
  context.lineCap = "round";
  context.lineJoin = "round";
  const strokeProjected = (points, color, width) => {
    if (!points || points.length < 2) return;
    context.beginPath();
    points.forEach((point, index) => {
      const projected = project(point);
      if (index === 0) context.moveTo(projected.x, projected.y);
      else context.lineTo(projected.x, projected.y);
    });
    context.strokeStyle = color;
    context.lineWidth = width;
    context.stroke();
  };
  splitTraveledPath(validPath).forEach((segment) => {
    strokeProjected(segment, "rgba(255,107,0,0.2)", 18);
    strokeProjected(segment, "#ff6b00", 7);
  });

  const start = project(validPath[0]);
  const end = project(validPath[validPath.length - 1]);
  [[start, "#22c55e"], [end, "#ef4444"]].forEach(([point, color]) => {
    context.fillStyle = color;
    context.beginPath();
    context.arc(point.x, point.y, 13, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = "#ffffff";
    context.lineWidth = 5;
    context.stroke();
  });

  context.restore();

  context.strokeStyle = "rgba(255,255,255,0.35)";
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(mapBounds.x, mapBounds.y, mapBounds.width, mapBounds.height, 18);
  context.stroke();

  context.fillStyle = "rgba(255,255,255,0.92)";
  context.fillRect(mapBounds.x + mapBounds.width - 292, mapBounds.y + mapBounds.height - 28, 292, 28);
  context.fillStyle = "#333333";
  context.font = "400 14px Arial";
  context.fillText(
    "© OpenStreetMap contributors",
    mapBounds.x + mapBounds.width - 278,
    mapBounds.y + mapBounds.height - 9,
  );

  const stats = [
    ["DISTANCE", `${summary.distanceKm.toFixed(1)} km`],
    ["DURATION", trip.duration || "—"],
    ["START", formatTripTime(trip.startTime) || "—"],
    ["END", formatTripTime(trip.endTime) || "—"],
  ];

  stats.forEach(([label, value], index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);
    const x = 1085 + column * 245;
    const y = 260 + row * 175;
    context.fillStyle = "rgba(255,255,255,0.08)";
    drawRoundedRect(context, x, y, 215, 140, 18);
    context.fillStyle = "#8fa7c2";
    context.font = "700 18px Arial";
    context.fillText(label, x + 22, y + 42);
    context.fillStyle = "#ffffff";
    context.font = "700 29px Arial";
    context.fillText(String(value), x + 22, y + 91, 175);
  });

  context.fillStyle = "#a9bdd5";
  context.font = "400 21px Arial";
  context.fillText("Generated from historical vehicle travel data", 72, 940);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not create trip image."))),
      "image/png",
    );
  });
};

/** Sample historicalPath at ~intervalMs gaps using timestamps */
const sampleWaypoints = (path, intervalMs = 30000) => {
  if (!path || path.length === 0) return [];

  const result = [];
  let lastTs = path[0].timestamp - intervalMs;

  path.forEach((pt, idx) => {
    if (pt.timestamp - lastTs >= intervalMs) {
      let speedKmh = 0;
      if (idx > 0) {
        const prev = path[idx - 1];
        const distM = haversineMeters(prev.lat, prev.lng, pt.lat, pt.lng);
        const timeSec = (pt.timestamp - prev.timestamp) / 1000;
        if (timeSec > 0) speedKmh = (distM / timeSec) * 3.6;
      }

      result.push({
        id: `wp_${pt.timestamp}_${idx}`,
        lat: pt.lat,
        lng: pt.lng,
        timestamp: pt.timestamp,
        speedKmh: parseFloat(speedKmh.toFixed(2)),
        formattedTime: formatTime(new Date(pt.timestamp)),
        formattedDate: formatWaypointDate(pt.timestamp),
      });
      lastTs = pt.timestamp;
    }
  });

  return result;
};

// ─── Map Legend Overlay ───────────────────────────────────────────────────────
const MapLegend = () => (
  <Box
    sx={{
      position: "absolute",
      bottom: 10,
      left: 10,
      background: "rgba(255,255,255,0.92)",
      borderRadius: "6px",
      px: 1.5,
      py: 0.8,
      display: "flex",
      gap: 1.5,
      alignItems: "center",
      boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
      flexWrap: "wrap",
      zIndex: 5,
    }}
  >
    {[
      { color: "#34A853", label: "Start" },
      { color: "#FF6B00", label: "Traveled", line: true },
      { color: "#4285F4", label: "History Points" },
      { color: "#EA4335", label: "End" },
    ].map(({ color, label, line }) => (
      <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {line ? (
          <Box sx={{ width: 18, height: 4, borderRadius: 2, background: color }} />
        ) : (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: color,
              border: "1.5px solid #fff",
              boxShadow: `0 0 0 1px ${color}`,
            }}
          />
        )}
        <Typography sx={{ fontSize: 10, color: "#555" }}>{label}</Typography>
      </Box>
    ))}
  </Box>
);

// ─── Mini Map ─────────────────────────────────────────────────────────────────
const TripMiniMap = ({ historicalPath, loadingPath, waypointIntervalMs = 30000 }) => {
  const [miniMap, setMiniMap] = useState(null);
  const [selectedWaypoint, setSelectedWaypoint] = useState(null);

  // Sort points by timestamp so the polyline always connects them in chronological order
  const sortedPath = useMemo(
    () => cleanTraveledPath(historicalPath),
    [historicalPath],
  );
  const traveledSegments = useMemo(
    () => splitTraveledPath(sortedPath),
    [sortedPath],
  );

  // Fit after the panel has its real size. Fitting while the row is still
  // opening stretches the path into a straight line.
  useEffect(() => {
    setSelectedWaypoint(null);
    if (!miniMap || sortedPath.length === 0) return undefined;
    let frame = 0;
    const fit = () => {
      window.google.maps.event.trigger(miniMap, "resize");
      const bounds = new window.google.maps.LatLngBounds();
      sortedPath.forEach((pt) => bounds.extend({ lat: pt.lat, lng: pt.lng }));
      miniMap.fitBounds(bounds, 40);
    };
    frame = window.requestAnimationFrame(fit);
    const container = miniMap.getDiv?.();
    const observer = container ? new ResizeObserver(fit) : null;
    if (container) observer.observe(container);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [miniMap, sortedPath]);

  const waypoints = useMemo(
    () => sampleWaypoints(sortedPath, waypointIntervalMs),
    [sortedPath, waypointIntervalMs],
  );

  const center =
    sortedPath.length > 0
      ? sortedPath[Math.floor(sortedPath.length / 2)]
      : { lat: 0, lng: 0 };

  if (loadingPath) {
    return (
      <Box
        sx={{
          width: "100%", height: MINI_MAP_HEIGHT,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "#f0f4f8", borderRadius: "20px", border: "1px solid #000000",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={32} sx={{ color: "#183059" }} />
          <Typography sx={{ mt: 1, fontSize: 12, color: "#666" }}>Loading path…</Typography>
        </Box>
      </Box>
    );
  }

  if (historicalPath.length === 0) {
    return (
      <Box
        sx={{
          width: "100%", height: MINI_MAP_HEIGHT,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "#f0f4f8", borderRadius: "20px", border: "1px solid #000000",
        }}
      >
        <Typography sx={{ fontSize: 13, color: "#999" }}>No path data available</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: MINI_MAP_HEIGHT,
        borderRadius: "20px",
        overflow: "hidden",
        border: "1px solid #000000",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        position: "relative",
        flexShrink: 0,
      }}
    >
      <GoogleMap
        mapContainerStyle={miniMapContainerStyle}
        center={center}
        zoom={13}
        onLoad={setMiniMap}
        options={{ ...mapOptions, minZoom: 1, styles: mapStyles }}
      >
        {traveledSegments.map((segment, index) => (
          <Polyline
            key={`traveled-${index}-${segment.length}`}
            path={segment}
            options={{
              strokeColor: "#FF6B00",
              strokeOpacity: 0.95,
              strokeWeight: 4,
              zIndex: 10,
              geodesic: true,
            }}
          />
        ))}

        {waypoints.map((wp) => (
          <Marker
            key={wp.id}
            position={{ lat: wp.lat, lng: wp.lng }}
            onClick={() => setSelectedWaypoint(wp)}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: "#4285F4",
              fillOpacity: 0.9,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
            }}
            zIndex={20}
          />
        ))}

        {selectedWaypoint && (
          <InfoWindow
            position={{ lat: selectedWaypoint.lat, lng: selectedWaypoint.lng }}
            onCloseClick={() => setSelectedWaypoint(null)}
            options={{ pixelOffset: new window.google.maps.Size(0, -10) }}
          >
            <div
              style={{
                padding: 0,
                minWidth: 220,
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              }}
            >
              <div style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>
                  {selectedWaypoint.formattedDate}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#1a73e8" }}>
                  {selectedWaypoint.formattedTime}
                </div>
              </div>
              <div
                style={{
                  backgroundColor: "#34a853", color: "white",
                  padding: "6px 10px", borderRadius: 4,
                  textAlign: "center", fontSize: 13, fontWeight: 600, marginBottom: 10,
                }}
              >
                Speed: {selectedWaypoint.speedKmh} km/h
              </div>
              <div
                style={{
                  fontSize: 11, color: "#666", display: "flex",
                  gap: 4, alignItems: "flex-start",
                  paddingTop: 8, borderTop: "1px solid #e0e0e0",
                }}
              >
                <span style={{ color: "#ea4335" }}>📍</span>
                <span>
                  {selectedWaypoint.lat.toFixed(6)}, {selectedWaypoint.lng.toFixed(6)}
                </span>
              </div>
            </div>
          </InfoWindow>
        )}

        <Marker
          position={sortedPath[0]}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8, fillColor: "#34A853", fillOpacity: 1,
            strokeColor: "#fff", strokeWeight: 2,
          }}
          title="Trip Start"
          zIndex={30}
        />
        <Marker
          position={sortedPath[sortedPath.length - 1]}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8, fillColor: "#EA4335", fillOpacity: 1,
            strokeColor: "#fff", strokeWeight: 2,
          }}
          title="Trip End"
          zIndex={30}
        />
      </GoogleMap>
      <MapLegend />
    </Box>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value }) => (
  <Box
    sx={{
      display: "flex", alignItems: "center", gap: 1.5,
      p: "10px 14px", borderRadius: "8px",
      background: "#F4F6FA", border: "1px solid #E0E6EF",
    }}
  >
    <Box sx={{ color: "#183059", display: "flex" }}>{icon}</Box>
    <Box>
      <Typography
        sx={{
          fontSize: 10, color: "#888", fontWeight: 500,
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#183059", mt: "1px" }}>
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

// ─── Expanded Trip Details Panel ──────────────────────────────────────────────
const TripExpandedRow = ({
  trip,
  historicalPath,
  loadingPath,
  waypointIntervalMs = 30000,
  vehicle,
  route,
}) => {
  const [exporting, setExporting] = useState(false);

  const handleDownload = async () => {
    if (exporting || loadingPath || historicalPath.length < 2) return;
    setExporting(true);
    try {
      const blob = await createTripSummaryImage({
        trip,
        historicalPath,
        vehicle,
        route,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const vehicleLabel = vehicle?.VehicleNumber || vehicle?.vehicleNumber || vehicle?.name || "vehicle";
      let organizationName = vehicle?.organization?.name || "";
      if (!organizationName) {
        try {
          organizationName = JSON.parse(localStorage.getItem("userState") || "{}")?.organization?.name || "";
        } catch {
          organizationName = "";
        }
      }
      const siteName = vehicle?.site?.name || vehicle?.site?.siteName || vehicle?.siteName || route?.site?.name || "";
      const sanitizeFilePart = (value) =>
        String(value || "")
          .trim()
          .replace(/[^a-z0-9_-]+/gi, "-")
          .replace(/^-+|-+$/g, "");
      link.href = url;
      const fileLabel = [organizationName, siteName, vehicleLabel, trip.date || "trip"]
        .map(sanitizeFilePart)
        .filter(Boolean)
        .join("-");
      link.download = `${fileLabel}-summary.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Unable to export trip summary image", error);
    } finally {
      setExporting(false);
    }
  };

  return (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2.5,
      p: "16px 20px 20px",
      background: "linear-gradient(135deg, #F8FBFF 0%, #EEF4FB 100%)",
      borderTop: "2px solid #D0E8F7",
    }}
  >
    {/* ── Left: Details ── */}
    <Box sx={{ flex: "0 0 42%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <RouteIcon sx={{ fontSize: 18, color: "#183059" }} />
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#183059" }}>
          Trip #{trip.id} Details
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}>
        {vehicle?.name && (
          <StatCard
            icon={<SpeedIcon sx={{ fontSize: 18 }} />}
            label="Vehicle Name"
            value={vehicle.name}
          />
        )}
        {(vehicle?.VehicleNumber || vehicle?.vehicleNumber) && (
          <StatCard
            icon={<SpeedIcon sx={{ fontSize: 18 }} />}
            label="Vehicle Number"
            value={vehicle?.VehicleNumber || vehicle?.vehicleNumber}
          />
        )}
        <StatCard
          icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
          label="Date"
          value={formatTripDate(trip.date)}
        />
        <StatCard
          icon={<TimerIcon sx={{ fontSize: 18 }} />}
          label="Duration"
          value={trip.duration || "—"}
        />
        <StatCard
          icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
          label="Start Time"
          value={formatTripTime(trip.startTime)}
        />
        <StatCard
          icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
          label="End Time"
          value={formatTripTime(trip.endTime)}
        />
        <Box sx={{ gridColumn: "1 / -1" }}>
          <StatCard
            icon={<RouteIcon sx={{ fontSize: 18 }} />}
            label="Route"
            value={trip.routeName && trip.routeName !== "N/A" ? trip.routeName : route?.name || "—"}
          />
        </Box>
      </Box>
    </Box>

    {/* ── Right: Mini Map ── */}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
          onClick={handleDownload}
          disabled={exporting || loadingPath || historicalPath.length < 2}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {exporting ? "Generating image…" : "Download trip image"}
        </Button>
      </Box>
      <TripMiniMap
        historicalPath={historicalPath}
        loadingPath={loadingPath}
        waypointIntervalMs={waypointIntervalMs}
      />
    </Box>
  </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PreviousTripsTable = ({
  trips,
  loading,
  selectedTrip,
  onTripClick,
  onClearSelection,
  loadingPath,
  historicalPath = [],
  isLoaded = false,
  historyPointInterval = 30000,
  vehicle = null,
  route = null,
  t={t},
  tripDate = "",
  onTripDateChange,
}) => {
  const [expandedId, setExpandedId] = useState(null);

  const handleRowClick = (trip) => {
    const isSame = expandedId === trip.id;
    if (isSame) {
      setExpandedId(null);
      onClearSelection();
    } else {
      setExpandedId(trip.id);
      onTripClick(trip);
    }
  };

  return (
    <Box sx={{ borderRadius: "4px", border: "1px solid #A5DCF3" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #A5DCF3",
          backgroundColor: "#F7FBFD",
        }}
      >
        <Typography sx={{ fontWeight: 600, color: "#183059" }}>
          {t("Completed Trips")}
        </Typography>
        <TextField
          label={t("Select Date")}
          type="date"
          size="small"
          value={tripDate || ""}
          onChange={(event) => onTripDateChange?.(event.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: format(new Date(), "yyyy-MM-dd") }}
          sx={{ backgroundColor: "#fff", minWidth: 180 }}
        />
      </Box>
      <TableContainer
        component={Paper}
        sx={{ boxShadow: "0px 2px 4px rgba(0,0,0,0.1)", maxHeight: 600 }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {["", "Sr. No", "Date", "Start Time", "End Time", "Duration"].map(
                (label, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: "#E8EAEE",
                      color: "#183059",
                      ...(i === 0 && { width: 40 }),
                    }}
                  >
                    {t(label)}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : trips.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">{t("No trips found for selected date")}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              trips.map((trip, index) => {
                const isExpanded = expandedId === trip.id;
                const isSelected = selectedTrip?.id === trip.id;

                return (
                  <React.Fragment key={trip.id}>
                    {/* ── Summary Row ── */}
                    <TableRow
                      onClick={() => handleRowClick(trip)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#FFF3E0" : "inherit",
                        "&:hover": { backgroundColor: isSelected ? "#FFE0B2" : "#F5F5F5" },
                        borderLeft: isExpanded ? "3px solid #FF6B00" : "3px solid transparent",
                        transition: "background-color 0.15s, border-left 0.15s",
                      }}
                    >
                      <TableCell sx={{ py: 0.8, pl: 1.5, color: "#888" }}>
                        {isExpanded ? (
                          <KeyboardArrowUpIcon fontSize="small" sx={{ color: "#FF6B00" }} />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{formatTripDate(trip.date)}</TableCell>
                      <TableCell>{formatTripTime(trip.startTime)}</TableCell>
                      <TableCell>{formatTripTime(trip.endTime)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                          {trip.duration}
                          {isExpanded && (
                            <Box
                              sx={{
                                width: 6, height: 6, borderRadius: "50%",
                                background: "#FF6B00",
                                animation: "pulse 1.4s infinite",
                                "@keyframes pulse": {
                                  "0%,100%": { opacity: 1 },
                                  "50%": { opacity: 0.3 },
                                },
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* ── Expanded Detail Row ── */}
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          p: 0,
                          verticalAlign: "top",
                          overflow: "hidden",
                          borderBottom: isExpanded ? "2px solid #D0E8F7" : "none",
                        }}
                      >
                        <Collapse in={isExpanded} timeout={300} unmountOnExit>
                          {isLoaded ? (
                            <TripExpandedRow
                              trip={trip}
                              historicalPath={isSelected ? historicalPath : []}
                              loadingPath={isSelected ? loadingPath : false}
                              waypointIntervalMs={historyPointInterval}
                              vehicle={vehicle}
                              route={route}
                            />
                          ) : (
                            <Box sx={{ p: 3, textAlign: "center" }}>
                              <CircularProgress size={20} />
                            </Box>
                          )}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PreviousTripsTable;
